/**
 * GraphQL Hooks for Users
 *
 * Apollo Client hooks for user operations with automatic
 * transformation to REST-compatible types for component compatibility.
 *
 * Feature: 063-graphql-frontend
 * Tasks: T027-T028
 */

import { useQuery } from '@apollo/client';
import { GET_USER_BY_USERNAME, GET_USER_PROFILE } from '../queries';
import { mapGraphQLPhotoToREST, mapGraphQLCollectionToREST } from '../mappers';
import type { GraphQLUser, GraphQLPhoto, GraphQLCollection, PaginatedResult } from '../types';
import type { User } from '../../types';
import type { Photo } from '../../types/photo';
import type { Collection } from '../../types/collection';

interface GetUserByUsernameData {
  userByUsername: GraphQLUser;
}

/**
 * Transform GraphQL User to REST User
 */
function mapGraphQLUserToREST(gqlUser: GraphQLUser): User {
  return {
    id: gqlUser.id,
    globalId: gqlUser.globalId,
    username: gqlUser.username,
    displayName: gqlUser.displayName,
    slug: gqlUser.slug,
    email: gqlUser.email,
    emailVerified: gqlUser.isVerified,
    createdAt: gqlUser.createdAt,
  };
}

/**
 * Query: Get user by username
 */
export function useUserGraphQL(username: string | undefined) {
  const { data, loading, error, refetch } = useQuery<GetUserByUsernameData>(
    GET_USER_BY_USERNAME,
    {
      variables: { username },
      skip: !username,
      fetchPolicy: 'cache-and-network',
    }
  );

  const user: User | undefined = data?.userByUsername
    ? mapGraphQLUserToREST(data.userByUsername)
    : undefined;

  return {
    data: user,
    isLoading: loading,
    isError: !!error,
    error,
    refetch,
  };
}

interface GetUserProfileData {
  userByUsername: GraphQLUser & {
    photos: PaginatedResult<GraphQLPhoto>;
    collections: PaginatedResult<GraphQLCollection>;
  };
}

interface UserProfileResult {
  user: User | undefined;
  photos: Photo[];
  collections: Collection[];
  photoCount: number;
  collectionCount: number;
}

/**
 * Query: Get user profile with photos and collections
 *
 * Used by UserProfilePage to display user's public profile.
 */
export function useUserProfileGraphQL(
  username: string | undefined,
  options?: {
    photosLimit?: number;
    photosOffset?: number;
    collectionsLimit?: number;
    collectionsOffset?: number;
  }
) {
  const {
    photosLimit = 50,
    photosOffset = 0,
    collectionsLimit = 50,
    collectionsOffset = 0,
  } = options || {};

  const { data, loading, error, refetch } = useQuery<GetUserProfileData>(
    GET_USER_PROFILE,
    {
      variables: {
        username,
        photosLimit,
        photosOffset,
        collectionsLimit,
        collectionsOffset,
      },
      skip: !username,
      fetchPolicy: 'cache-and-network',
    }
  );

  const result: UserProfileResult = {
    user: data?.userByUsername
      ? mapGraphQLUserToREST(data.userByUsername)
      : undefined,
    photos: data?.userByUsername?.photos?.items
      ? data.userByUsername.photos.items.map(mapGraphQLPhotoToREST)
      : [],
    collections: data?.userByUsername?.collections?.items
      ? data.userByUsername.collections.items.map(mapGraphQLCollectionToREST)
      : [],
    photoCount: data?.userByUsername?.photos?.totalCount || 0,
    collectionCount: data?.userByUsername?.collections?.totalCount || 0,
  };

  return {
    data: result,
    isLoading: loading,
    isError: !!error,
    error,
    refetch,
  };
}
